import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Award, Shield, LineChart, Gauge, Zap, RefreshCw, Trophy, Sparkles, Sun, Users, GraduationCap, ShieldCheck, Leaf, Landmark, Star, Handshake, Wallet, Battery, Palmtree, Crown, MapPin, Truck } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const IMG = {
    hero: "/generated/usp_hero_partner.png",
    founders: "/generated/usp_founders_summit.png",
    dashboard: "/generated/usp_investor_dashboard.png",
    fleet: "/generated/usp_fleet_charging.png",
    technician: "/generated/usp_technician_service.png",
    hospitality: "/generated/usp_hospitality_partner.png",
    driver: "/generated/usp_ev_driver_urban.png",
    ceremony: "/generated/usp_ceremony_certification.png",
    premiumHub: "/generated/e3_premium_hub.png",
    resort: "/generated/e3_resort_charger.png",
};

// 24 differentiators grouped into 5 themed pillars
const PILLARS = [
    {
        pillar: "Partnership · Community",
        overline: "Pillar 01",
        image: IMG.founders,
        headline: "Not a franchise. A community of owners.",
        body: "Most CPOs treat you as a location. E3 treats you as a co-builder of infrastructure — with lifetime protections, direct access and shared upside.",
        items: [
            { icon: Crown, title: "Founding Partner Program", desc: "First 100 partners: lifetime preferential pricing, priority expansion rights, annual Founder's Summit.", highlight: true },
            { icon: Shield, title: "Territory Protection", desc: "Defined territory radius. Priority rights on future sites. Right of first refusal." },
            { icon: Landmark, title: "E3 Capital Connect™", desc: "Introductions to banks, NBFCs, infra funds and green finance for scaling partners.", highlight: true },
            { icon: Star, title: "E3 Founder's Circle™", desc: "3+ / 5+ / 10+ station owners: private retreats, early access, co-investment rights.", highlight: true },
            { icon: Handshake, title: "Community Ownership", desc: "Future co-investment in premium hubs — a new asset class few in India offer." },
            { icon: LineChart, title: "E3 Investor Dashboard", desc: "Live revenue, utilisation, ROI, carbon and monthly performance reports." },
            { icon: Landmark, title: "Infrastructure Asset Model", desc: "Position chargers as income-generating infrastructure assets with annual valuation reports and benchmarking." },
        ],
    },
    {
        pillar: "Revenue · Growth Engine",
        overline: "Pillar 02",
        image: IMG.fleet,
        headline: "We bring the customers to your chargers.",
        body: "E3 doesn't just sell you a machine. Our central business development engine drives fleet contracts, corporate tie-ups and app-based demand into your station.",
        items: [
            { icon: ShieldCheck, title: "Revenue Guarantee Support", desc: "Below-threshold utilisation in Year 1? E3 provides fleet leads, campaigns and corporate tie-ups." },
            { icon: Truck, title: "Fleet Charging Marketplace", desc: "Direct pipeline of logistics, taxi and corporate fleet accounts routed to partner stations." },
            { icon: Users, title: "E3 Business Development Team", desc: "Central BD desk that lands enterprise customers and app downloads on your behalf." },
            { icon: Wallet, title: "E3 Plus Membership", desc: "Drivers pay annual dues for discounted charging, loyalty points and reservations — recurring revenue." },
            { icon: MapPin, title: "Destination Partner Program", desc: "Hotels & resorts get app listings, campaigns and EV-tourism promotion." },
        ],
    },
    {
        pillar: "Operations · Infrastructure",
        overline: "Pillar 03",
        image: IMG.technician,
        headline: "OEM-grade uptime. Built to last a decade.",
        body: "Direct Cubenz OEM support means faster response, deeper diagnostics and access to hardware roadmap upgrades — not a third-party service window.",
        items: [
            { icon: Gauge, title: "99% Uptime Commitment", desc: "Preventive maintenance, remote monitoring and priority breakdown response backed by Cubenz OEM." },
            { icon: Zap, title: "Charger Upgrade Program", desc: "Move 120 → 180 → 240 kW at preferential pricing as tech evolves." },
            { icon: RefreshCw, title: "Trade-In Program", desc: "Old chargers eligible for buyback / refurbishment — a first in India." },
            { icon: Sun, title: "Power Security Program", desc: "Solar integration, battery storage and DG backup for premium and remote sites." },
            { icon: ShieldCheck, title: "Charger Insurance Program", desc: "Bundled equipment insurance, revenue interruption cover and third-party liability." },
            { icon: GraduationCap, title: "E3 Academy", desc: "Certified training for operators, technicians and franchisees. Industry-recognised badges." },
        ],
    },
    {
        pillar: "Rewards · Recognition",
        overline: "Pillar 04",
        image: IMG.ceremony,
        headline: "Prestige, awards, ESG credentials.",
        body: "Ownership on E3 isn't transactional. Top-performing partners are recognised, rewarded and given a platform — creating enduring brand value.",
        items: [
            { icon: Trophy, title: "Performance Bonus Program", desc: "Cross utilisation targets → reduced AMC, cashback and loyalty credits." },
            { icon: Sparkles, title: "E3 Rewards Program", desc: "Earn points on energy sold, uptime and ratings — redeem against AMC, spares or new stations." },
            { icon: Award, title: "E3 Certified Site Program", desc: "Annual Gold, Platinum, Best Destination and Best Highway station awards." },
            { icon: Leaf, title: "Carbon Impact Dashboard", desc: "Annual ESG certificate with CO₂ avoided, green kilometres and renewable impact metrics." },
        ],
    },
    {
        pillar: "Beyond Charging",
        overline: "Pillar 05",
        image: IMG.hospitality,
        headline: "An infrastructure platform, not a machine.",
        body: "The station is only the beginning. E3 unlocks hospitality footfall, energy services, ESG credits and future revenue streams that pure-play CPOs cannot.",
        items: [
            { icon: Palmtree, title: "Charging + Hospitality Model", desc: "Hotels & restaurants gain footfall, F&B and retail uplift alongside charging revenue." },
            { icon: Battery, title: "Future Energy Services", desc: "Carbon credits, RECs, battery swapping and V2G readiness — new revenue streams built in." },
        ],
    },
];

const COMPARISON = [
    { p: "Franchise Ownership", cz: "Limited", st: "Limited", e3: "Full asset ownership" },
    { p: "OEM Support", cz: "Third Party", st: "Third Party", e3: "Direct OEM from Cubenz" },
    { p: "Warranty", cz: "Standard", st: "Standard", e3: "Extended warranty options" },
    { p: "Annual Maintenance", cz: "Paid", st: "Paid", e3: "Included for initial period" },
    { p: "Revenue Sharing", cz: "Fixed", st: "Fixed", e3: "Flexible models" },
    { p: "White Label", cz: "No", st: "No", e3: "Yes" },
    { p: "Destination Charging Focus", cz: "Limited", st: "Moderate", e3: "Core Focus" },
    { p: "Site Selection Assistance", cz: "Limited", st: "Yes", e3: "Dedicated support" },
    { p: "Network Design", cz: "Advisory", st: "Limited", e3: "Included" },
    { p: "Training Support", cz: "Limited", st: "Limited", e3: "Included" },
    { p: "Energy Management", cz: "No", st: "No", e3: "Included" },
    { p: "Fleet Opportunities", cz: "Limited", st: "Moderate", e3: "High" },
    { p: "Carbon Credits", cz: "No", st: "No", e3: "Future Program" },
    { p: "Partner Loyalty Program", cz: "No", st: "No", e3: "Yes" },
    { p: "Regional Exclusivity", cz: "Rare", st: "Rare", e3: "Possible" },
    { p: "Charger OEM Access", cz: "No", st: "No", e3: "Direct" },
];

export const USP = () => {
    let cardIndex = 0;
    return (
        <div data-testid="usp-page">
            {/* HERO with full-bleed partner portrait */}
            <section className="relative min-h-[85vh] flex items-end overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG.hero})` }} aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-950/40 to-stone-950/20" aria-hidden="true" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-24 w-full">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs tracking-[0.2em] uppercase mb-8 animate-fade-up">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Why E3
                    </div>
                    <h1 data-testid="usp-hero-title" className="font-serif text-white text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] max-w-5xl animate-fade-up">
                        24 reasons to choose <span className="italic e3-gradient-text">E3.</span>
                    </h1>
                    <p className="text-white/85 text-lg md:text-xl leading-relaxed max-w-3xl mt-8 animate-fade-up">
                        Most CPOs compete on chargers, apps, pricing and locations. Very few have a
                        compelling partner value proposition. E3 does — and here's how.
                    </p>
                </div>
            </section>

            {/* Positioning quote */}
            <section className="py-24 px-6 md:px-10 bg-stone-50">
                <div className="max-w-5xl mx-auto text-center">
                    <div className="e3-overline mb-6">Positioning</div>
                    <blockquote className="font-serif text-2xl md:text-4xl font-light tracking-tight text-stone-900 leading-[1.25] italic">
                        "ChargeZone sells charging. Statiq sells network access. <span className="not-italic font-medium e3-gradient-text">E3 builds a community of infrastructure owners</span> participating in India's electric mobility transition."
                    </blockquote>
                </div>
            </section>

            {/* PILLARS — each with a hero image + grid of cards */}
            {PILLARS.map((pillar, pIdx) => {
                const isEven = pIdx % 2 === 0;
                return (
                    <section
                        key={pillar.pillar}
                        className={`py-24 md:py-32 px-6 md:px-10 ${pIdx % 2 === 1 ? "bg-stone-50" : ""}`}
                        data-testid={`usp-pillar-${pIdx}`}
                    >
                        <div className="max-w-7xl mx-auto">
                            {/* Pillar header split */}
                            <div className={`grid grid-cols-1 md:grid-cols-12 gap-10 items-center mb-16 ${!isEven ? "md:[&>div:first-child]:order-2" : ""}`}>
                                <div className="md:col-span-6">
                                    <div className="rounded-2xl overflow-hidden aspect-[4/3] shadow-xl">
                                        <img src={pillar.image} alt={pillar.headline} className="w-full h-full object-cover" data-testid={`usp-pillar-${pIdx}-image`} />
                                    </div>
                                </div>
                                <div className="md:col-span-6">
                                    <div className="flex items-center gap-3 mb-6">
                                        <span className="font-mono text-xs text-stone-500 tabular-nums">{pillar.overline}</span>
                                        <span className="w-8 h-px bg-stone-300" />
                                        <span className="e3-overline">{pillar.pillar}</span>
                                    </div>
                                    <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tighter leading-[1.05] text-stone-900 mb-6">
                                        {pillar.headline.split(".").map((part, i, arr) => (
                                            <span key={i}>
                                                {i === arr.length - 2 ? (
                                                    <>
                                                        <span className="italic e3-gradient-text">{part}</span>{i < arr.length - 1 ? "." : ""}
                                                    </>
                                                ) : (
                                                    <>{part}{i < arr.length - 1 ? "." : ""}</>
                                                )}
                                            </span>
                                        ))}
                                    </h2>
                                    <p className="text-stone-600 text-lg leading-relaxed">{pillar.body}</p>
                                </div>
                            </div>

                            {/* Differentiator cards */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
                                {pillar.items.map((d) => {
                                    const Icon = d.icon;
                                    const idx = cardIndex++;
                                    return (
                                        <div
                                            key={d.title}
                                            data-testid={`usp-card-${idx}`}
                                            className={`e3-card group ${d.highlight ? "border-emerald-300 bg-emerald-50/40" : ""}`}
                                        >
                                            <div className="flex items-start justify-between mb-6">
                                                <div className="w-11 h-11 rounded-xl e3-gradient-soft flex items-center justify-center group-hover:scale-110 transition-transform">
                                                    <Icon className="w-5 h-5 text-emerald-700" strokeWidth={1.5} />
                                                </div>
                                                <div className="text-xs font-mono text-stone-400 tabular-nums">{String(idx + 1).padStart(2, "0")}</div>
                                            </div>
                                            <h3 className="font-serif text-xl font-medium text-stone-900 mb-3 leading-tight">{d.title}</h3>
                                            <p className="text-stone-600 text-sm leading-relaxed">{d.desc}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                );
            })}

            {/* INVESTOR DASHBOARD callout — dedicated split section */}
            <section className="py-24 md:py-32 px-6 md:px-10 bg-stone-950 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #6abf4b 0px, transparent 40%), radial-gradient(circle at 80% 70%, #2b8fb0 0px, transparent 40%)" }} />
                <div className="max-w-7xl mx-auto relative grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-6">
                        <div className="rounded-2xl overflow-hidden aspect-[4/3] shadow-2xl">
                            <img src={IMG.dashboard} alt="E3 Investor Dashboard being reviewed" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <div className="e3-overline mb-6 text-stone-400">Live · E3 Investor Dashboard</div>
                        <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tighter leading-[1.05] mb-6">
                            See every kWh, every ₹, every kg CO₂ — <span className="italic e3-gradient-text">from your phone.</span>
                        </h2>
                        <p className="text-stone-300 text-lg leading-relaxed mb-8">
                            The E3 Investor Dashboard turns your infrastructure asset into a fully
                            transparent investment. Revenue, utilisation, uptime, carbon avoided —
                            live, benchmarked, and delivered monthly to your inbox.
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                            {["Live revenue", "Utilisation heatmaps", "Monthly ROI", "Carbon impact"].map((f) => (
                                <div key={f} className="flex items-center gap-2 text-stone-300">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {f}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* COMPARISON */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-7xl mx-auto">
                    <div className="e3-overline mb-6">Comparison</div>
                    <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900 mb-6 max-w-4xl">
                        How E3 stacks up.
                    </h2>
                    <p className="text-stone-600 text-lg leading-relaxed mb-12 max-w-2xl">
                        We're not another franchise. We're an infrastructure investment platform.
                    </p>

                    <div className="rounded-2xl overflow-hidden border border-stone-200 bg-white shadow-sm">
                        <Table data-testid="usp-comparison-table">
                            <TableHeader>
                                <TableRow className="bg-stone-100 hover:bg-stone-100">
                                    <TableHead className="text-stone-700 font-semibold py-5">Parameter</TableHead>
                                    <TableHead className="text-stone-700 font-semibold">ChargeZone</TableHead>
                                    <TableHead className="text-stone-700 font-semibold">Statiq</TableHead>
                                    <TableHead className="e3-gradient-text font-bold text-base">E3 Network</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {COMPARISON.map((row, i) => (
                                    <TableRow key={i} className="border-stone-200">
                                        <TableCell className="font-medium text-stone-900 py-4">{row.p}</TableCell>
                                        <TableCell className="text-stone-500">{row.cz}</TableCell>
                                        <TableCell className="text-stone-500">{row.st}</TableCell>
                                        <TableCell className="text-emerald-700 font-semibold bg-emerald-50/60">{row.e3}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </section>

            {/* Urban driver moment — closing brand image */}
            <section className="relative min-h-[65vh] flex items-end overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG.driver})` }} aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/85 via-stone-950/30 to-transparent" aria-hidden="true" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-24 w-full">
                    <h2 className="font-serif text-white text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] max-w-4xl">
                        Every station on our network powers <span className="italic e3-gradient-text">someone's journey.</span>
                    </h2>
                    <p className="text-white/80 text-lg mt-6 max-w-2xl">
                        Yours could be the next one.
                    </p>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-6xl mx-auto e3-gradient-bg rounded-3xl p-12 md:p-20 text-white relative overflow-hidden">
                    <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
                    <div className="relative">
                        <div className="e3-overline mb-6 text-white/70">Join the Community</div>
                        <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] mb-6 max-w-3xl">
                            Ready to become a Founding Partner?
                        </h2>
                        <p className="text-white/85 text-lg md:text-xl max-w-2xl mb-10">
                            The first 100 partners shape the future of the E3 network — with lifetime
                            benefits and access no future partner will receive.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <Link to="/franchise" data-testid="usp-cta-franchise" className="bg-white text-stone-900 px-8 py-3.5 rounded-full font-semibold hover:bg-stone-100 inline-flex items-center gap-2">
                                Explore Franchise Models <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link to="/contact" data-testid="usp-cta-contact" className="bg-white/10 border border-white/30 text-white px-8 py-3.5 rounded-full font-semibold hover:bg-white/20 inline-flex items-center gap-2">
                                Talk to Us
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};
